import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';

type Props = {
  children: ReactNode;
};

export type ClientBudget = {
  monthlyIncome: number;
  monthlyExpenses: number;
  numberOfEmployedHouseholdMembers: number;
  numberOfHouseholdMembers: number;
};

export type PostClientBudgetRequest = {
  body: ClientBudget;
};

export type ClientBudgetContextType = {
  clientBudgetInfo?: ClientBudget;
  fetchClientBudgetInfo: () => Promise<ClientBudget | void>;
  postClientBudget: ({
    body,
  }: PostClientBudgetRequest) => Promise<ClientBudget | void>;
  clientBudgetPsd?: ClientBudget;
  fetchClientBudgetPsd: () => Promise<ClientBudget | void>;
};

export const ClientBudgetContext = createContext<ClientBudgetContextType>(
  {} as ClientBudgetContextType,
);

export function ClientBudgetProvider({ children }: Props): React.JSX.Element {
  const [clientBudgetInfo, setClientBudgetInfo] = useState<ClientBudget>();
  const [clientBudgetPsd, setClientBudgetPsd] = useState<ClientBudget>();
  const { showError } = useError();

  const fetchClientBudgetInfo =
    useCallback(async (): Promise<ClientBudget | void> => {
      try {
        const fetchedClientBudgetInfo = await fetchData(`/client/budget/info`, {
          headers: {
            Accept: 'application/json',
          },
        });

        setClientBudgetInfo(fetchedClientBudgetInfo);

        return fetchedClientBudgetInfo;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  const fetchClientBudgetPsd =
    useCallback(async (): Promise<ClientBudget | void> => {
      try {
        const fetchedClientBudgetPsd = await fetchData(`/client/budget/psd`, {
          headers: {
            Accept: 'application/json',
          },
        });

        setClientBudgetPsd(fetchedClientBudgetPsd);

        return fetchedClientBudgetPsd;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  const postClientBudget: ClientBudgetContextType['postClientBudget'] =
    useCallback(
      async ({ body }) => {
        try {
          const resultClientBudget = await fetchData(`/client/budget`, {
            method: 'post',
            headers: {
              Accept: 'application/json',
            },
            body: JSON.stringify(body),
          });

          setClientBudgetInfo(resultClientBudget);
          return resultClientBudget;
        } catch (e) {
          showError();
          throw e;
        }
      },
      [showError],
    );

  const clientBudgetContextValue = useMemo(
    () => ({
      clientBudgetInfo,
      clientBudgetPsd,
      fetchClientBudgetPsd,
      fetchClientBudgetInfo,
      postClientBudget,
    }),
    [
      clientBudgetInfo,
      fetchClientBudgetInfo,
      postClientBudget,
      clientBudgetPsd,
      fetchClientBudgetPsd,
    ],
  );

  return (
    <ClientBudgetContext.Provider value={clientBudgetContextValue}>
      {children}
    </ClientBudgetContext.Provider>
  );
}
